import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, Ruler, Gauge, Layers, Wrench, Leaf, CheckCircle2,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const HEADLINE = [
  { v: "120 t", l: "Max point load" },
  { v: "600 psi", l: "Compressive strength" },
  { v: "10+ yrs", l: "Service life" },
  { v: "0%", l: "Fluid absorption" },
];

const GROUPS = [
  {
    icon: Ruler, title: "Physical & Material",
    rows: [
      ["Panel dimensions", "8 ft × 14 ft (2.44 × 4.27 m)"],
      ["Nominal coverage", "112 sq ft / panel"],
      ["Thickness", "4.0 in (102 mm)"],
      ["Unit weight", "975 lb (442 kg)"],
      ["Material", "UV-stabilized HDPE composite"],
      ["Recyclability", "100% recyclable"],
    ],
  },
  {
    icon: Gauge, title: "Structural Performance",
    rows: [
      ["Max point load", "120 tons (108,862 kg)"],
      ["Compressive strength", "up to 600 psi"],
      ["Flexural modulus", "≥ 110,000 psi"],
      ["Operating temperature", "−40°F to +200°F"],
      ["Heavy-tracking rating", "No cracking under repeated load"],
      ["Typical service life", "10+ years"],
    ],
  },
  {
    icon: Layers, title: "Surface & Utilization",
    rows: [
      ["Tread", "Dual-sided aggressive traction (reversible)"],
      ["Porosity", "Non-porous — zero contamination"],
      ["Chemical resistance", "Fuels, oils, most acids & alkalis"],
      ["Decontamination", "Pressure / steam washdown compatible"],
      ["Slip resistance", "High-traction, wet & dry"],
      ["Terrain", "Multi-terrain, instant relocation"],
    ],
  },
  {
    icon: Wrench, title: "Installation & Handling",
    rows: [
      ["Connection system", "Integrated cam-lock + overlap flange (tool-free)"],
      ["Placement equipment", "Skid steer, telehandler, excavator"],
      ["Typical crew", "2-person"],
      ["Install rate", "Up to 1,000 sq ft / crew-hour"],
      ["Lifting points", "Molded cam sockets / pockets"],
      ["Transport", "Stackable & nestable"],
    ],
  },
];

const ENV = [
  "Non-porous, non-leaching — protects sensitive soils & waterways",
  "Recyclable HDPE construction",
  "SWPPP-compatible ground protection",
  "OSHA-friendly walking / working surface",
];

const SKUS = [
  ["BBM-814", "8' × 14' × 4.0\"", "975 lb", "Heavy civil, crane pads, haul roads"],
  ["BBM-48", "4' × 8' × 0.5\"", "86 lb", "Light access, turf & pedestrian"],
  ["BBM-ACC", "Cam pins / clips / ramps", "—", "Connectors & edge transitions"],
];

export default function TechnicalDataSheet() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Technical Data Sheet</span>
            <a href="/big-bad-mats-technical-data-sheet.pdf" download data-testid="download-datasheet" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-datasheet" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 PORTRAIT SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="datasheet-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[210mm] h-[297mm] p-[13mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
              </div>
              <div className="inline-flex items-center border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.2em]">Technical Data Sheet</div>
              <h1 className="mt-2 font-display font-black tracking-tight text-ink leading-none text-[30px]">Big Bad Mats</h1>
              <p className="mt-1 font-display font-bold text-charcoal text-[12px]">Model BBM-814 · Heavy-Duty Composite Access Mat</p>
            </div>
            <img src={IMAGES.bigbadmats} alt="Big Bad Mats" className="w-40 h-28 object-cover border border-border" />
          </header>

          {/* headline specs */}
          <div className="grid grid-cols-4 gap-2 mt-3">
            {HEADLINE.map((h) => (
              <div key={h.l} className="border border-border bg-muted/40 p-2.5 text-center">
                <div className="font-display font-extrabold text-flame text-[20px] leading-none">{h.v}</div>
                <div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-1">{h.l}</div>
              </div>
            ))}
          </div>

          {/* spec groups 2x2 */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {GROUPS.map((g) => (
              <div key={g.title} className="border border-border">
                <div className="bg-ink text-white px-3 py-1.5 flex items-center gap-2">
                  <g.icon className="h-3.5 w-3.5 text-flame" />
                  <span className="font-display font-bold text-[11px] uppercase tracking-wider">{g.title}</span>
                </div>
                <dl>
                  {g.rows.map(([k, v], i) => (
                    <div key={k} className={`grid grid-cols-[0.85fr_1.15fr] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                      <dt className="px-3 py-1.5 text-muted-foreground text-[9.5px]">{k}</dt>
                      <dd className="px-3 py-1.5 font-mono text-ink text-[9.5px] border-l border-border">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          {/* environmental + SKUs */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="border border-border">
              <div className="bg-ink text-white px-3 py-1.5 flex items-center gap-2">
                <Leaf className="h-3.5 w-3.5 text-flame" />
                <span className="font-display font-bold text-[11px] uppercase tracking-wider">Environmental & Compliance</span>
              </div>
              <ul className="p-3 space-y-1.5">
                {ENV.map((e) => (
                  <li key={e} className="flex items-start gap-2 text-charcoal text-[9.5px] leading-snug">
                    <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-px" /> {e}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-border">
              <div className="bg-ink text-white px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider">Configurations</div>
              <div className="grid grid-cols-[0.7fr_1fr_0.6fr_1.4fr] bg-muted text-[8px] uppercase tracking-wider text-muted-foreground">
                <div className="px-2 py-1">SKU</div>
                <div className="px-2 py-1">Size</div>
                <div className="px-2 py-1">Weight</div>
                <div className="px-2 py-1">Application</div>
              </div>
              {SKUS.map((s, i) => (
                <div key={s[0]} className={`grid grid-cols-[0.7fr_1fr_0.6fr_1.4fr] text-[9px] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                  <div className="px-2 py-1.5 font-mono font-semibold text-flame">{s[0]}</div>
                  <div className="px-2 py-1.5 font-mono text-ink">{s[1]}</div>
                  <div className="px-2 py-1.5 font-mono text-ink">{s[2]}</div>
                  <div className="px-2 py-1.5 text-charcoal">{s[3]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-auto pt-3">
            <div className="bg-ink text-white flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <div className="font-display font-extrabold text-[14px] leading-tight">Need certified test data or a project-specific load analysis?</div>
                <div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 print:hidden">
                <Link to="/big-bad-mats-roi" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors">Lifecycle ROI</Link>
                <Link to="/request-quote" className="inline-flex items-center border border-white/30 text-white px-4 py-2.5 font-semibold text-[11px] hover:border-flame hover:text-flame transition-colors">Request Quote</Link>
              </div>
            </div>
            <p className="mt-2 text-[8px] text-muted-foreground leading-tight text-center">
              Specifications are representative and provided for evaluation; confirm certified values, tolerances &amp; load ratings for your application. © {new Date().getFullYear()} {COMPANY.legal}. {COMPANY.tagline}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
